import {
	GQLMerchTileFragment,
	GQLOrderItemFragment,
	GQLProductFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductSideSideType,
} from '@theprintbar/graphql';
import {
	Decoration,
	getAvailableSideDecoration,
	getFirstSamedayColour,
	isDTFDec,
	isDTGDec,
	isEMBDec,
	isLabel,
	isSCRDec,
	isSleeve,
} from '../..';
import { cloneDeep, invariant } from '../../..';
import { Merch, MerchFormServer } from '../schema';

export function merchQuantity(merch?: Merch) {
	if (merch?.teamNames?.selected?.length) {
		return merch.teamNames.selected.reduce((acc, member) => acc + (member.quantity || 0), 0);
	}
	return merch?.sizing.reduce((acc, size) => acc + size.quantity, 0) || 0;
}

export function hasDecoration(decoration: Decoration, sameday?: boolean) {
	return (
		!!decoration.artwork?.size &&
		!!decoration.meta.width &&
		!!decoration.meta.height &&
		(!sameday || decorationIsSamedayCompatible(decoration))
	);
}

export function hasDecorations(merch: Merch) {
	return !!merch.decorations.some(decoration => hasDecoration(decoration, merch.sameday));
}

interface MerchSamedayCompatibility {
	product: GQLProductFragment;
	merch?: Merch;
}
export function merchIsSamedayCompatible({ product, merch }: MerchSamedayCompatibility) {
	return {
		colourCompatible: !merch ? true : merchColourIsSameDayCompatible(merch, product),
		decorationCompatible: !merch
			? true
			: merch.decorations.every(decoration => !hasDecoration(decoration) || decorationIsSamedayCompatible(decoration)),
	};
}

export function decorationIsSamedayCompatible(decoration: { side?: any; type?: any }) {
	return !isSleeve(decoration.side) && !isLabel(decoration.side) && decoration.type === GQLProductSideAvailableDecorationType.Dtg;
}

export function decorationMethodIsSamedayCompatible(product: GQLProductFragment, method: GQLProductSideAvailableDecorationType) {
	return method === GQLProductSideAvailableDecorationType.Dtg && !!getFirstSamedayColour(product);
}

export function merchColourIsSameDayCompatible(merch: Merch, product: GQLProductFragment) {
	const productColour = product.colours?.find(colour => colour.colour.code === merch.colour);
	return !!productColour?.isSameday;
}

export function makeMerchSamedayCompatible(merch: Merch, product: GQLProductFragment) {
	const newMerch = cloneDeep(merch);

	newMerch.decorations = newMerch.decorations?.map(dec => {
		return isSleeve(dec.side) || isLabel(dec.side) ? dec : { ...dec, type: GQLProductSideAvailableDecorationType.Dtg };
	});

	if (!merchColourIsSameDayCompatible(merch, product)) {
		const stockedSameday = getFirstSamedayColour(product);
		invariant(stockedSameday, 'No sameday compatible colours found');
		newMerch.colour = stockedSameday?.colour.code;
	}

	return newMerch;
}

export function decorationResized(oldDecoration: Decoration, newDecoration: Decoration) {
	return oldDecoration.meta.height !== newDecoration.meta.height || oldDecoration.meta.width !== newDecoration.meta.width;
}

export function decorationMoved(oldDecoration: Decoration, newDecoration: Decoration) {
	return oldDecoration.meta.left !== newDecoration.meta.left || oldDecoration.meta.top !== newDecoration.meta.top;
}

export async function embHasChanged(newDecoration: Decoration, oldDecoration?: Decoration) {
	return (
		newDecoration.type === GQLProductSideAvailableDecorationType.Emb &&
		(!oldDecoration?.artwork?.size ||
			oldDecoration.type !== newDecoration.type ||
			!!newDecoration.error ||
			decorationResized(newDecoration, oldDecoration))
		// !(await BlobsMatch(newDecoration.artwork, oldDecoration.artwork)))
	);
}

export const merchHasDTG = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	!!merch?.decorations.some(d => isDTGDec(d, true));
export const merchHasDTF = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	!!merch?.decorations.some(d => isDTFDec(d, true));
export const merchHasSCR = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	!!merch?.decorations.some(d => isSCRDec(d, true));
export const merchHasEMB = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	!!merch?.decorations.some(d => isEMBDec(d, true));

export const merchDTGCount = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	merch?.decorations.filter(d => isDTGDec(d, true)).length || 0;
export const merchDTFCount = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	merch?.decorations.filter(d => isDTFDec(d, true)).length || 0;
export const merchSCRCount = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	merch?.decorations.filter(d => isSCRDec(d, true)).length || 0;
export const merchEMBCount = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	merch?.decorations.filter(d => isEMBDec(d, true)).length || 0;

export const merchDTGDecorations = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	merch?.decorations.filter(d => isDTGDec(d, true)) || [];
export const merchDTFDecorations = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	merch?.decorations.filter(d => isDTFDec(d, true)) || [];
export const merchSCRDecorations = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	merch?.decorations.filter(d => isSCRDec(d, true)) || [];
export const merchEMBDecorations = (merch?: Merch | MerchFormServer | GQLMerchTileFragment | null) =>
	merch?.decorations.filter(d => isEMBDec(d, true)) || [];

export const isMerchTileGuard = (
	merch: Merch | MerchFormServer | GQLMerchTileFragment | GQLOrderItemFragment | null,
): merch is GQLMerchTileFragment => !!(merch as GQLMerchTileFragment).id && !!(merch as GQLMerchTileFragment).decorations;
